import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TARS_MODULES } from "./InvoiceManagement/constants/modules";
import { fetchBouncerPermissions } from "../services/bouncerService";
import { savePermissionsToStorage } from "../context/PermissionsContext";
import { BOUNCER_PERMISSIONS } from "../constants/bouncerPermissions";
import { resolveReturnTo } from "./redirectResolver";
import { Box, CircularProgress } from "@mui/material";
import { COLOR_CONSTANTS } from "../helpers/Colors";
import auth from "../services/auth";

/**
 * Determines whether the user is an internal AP user (vs an external vendor).
 * Internal users have ACCOUNTS_PAYABLE_VIEW.
 */
export function isInternalUser(permissions) {
  return permissions.includes(BOUNCER_PERMISSIONS.ACCOUNTS_PAYABLE_VIEW);
}

/**
 * Get accessible modules for any user.
 * Production modules (production: true) are always included — visibility
 * is controlled by the KWS facility check in Sidebar at runtime.
 * Non-production modules are Bouncer-gated.
 */
export function getAccessibleModules(permissions) {
  const permissionSet = new Set(permissions);
  return TARS_MODULES.filter((module) =>
    module.production ||
    (Array.isArray(module.bouncerKey)
      ? module.bouncerKey.some((key) => permissionSet.has(key))
      : permissionSet.has(module.bouncerKey))
  );
}

export const Callback = () => {
  let navigate = useNavigate(),
    location = useLocation();
  const state = JSON.parse(
    decodeURIComponent(location.search.split("&")[2].split("=")[1])
  );
  let REDIRECT_URI = state.pathname;
  const REDIRECT_QUERY = state.search;
  const queryString = window.location.search.substring(1);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function initAuth() {
      // Step 1: OAuth login + token exchange
      let userDetails;
      try {
        userDetails = await auth.authorize();
      } catch (err) {
        console.error("Auth failed:", err);
        setError("Authentication failed. Please try logging in again.");
        setLoading(false);
        return;
      }
      if (!userDetails?.email) {
        setLoading(false);
        return;
      }

      // Step 2: Fetch all Bouncer permissions in one call (retry once on failure)
      let permissions = [];
      try {
        permissions = await fetchBouncerPermissions();
      } catch (err) {
        console.error("Failed to fetch Bouncer permissions:", err);
        try {
          permissions = await fetchBouncerPermissions();
        } catch (retryErr) {
          console.error("Bouncer permission retry failed:", retryErr);
          setError("Unable to load permissions. Please try logging in again.");
          setLoading(false);
          return;
        }
      }

      // Step 3: Save permissions to sessionStorage (for PermissionsProvider in Dashboard)
      savePermissionsToStorage(permissions);

      // Step 3b: Determine internal vs vendor user
      const isInternal = isInternalUser(permissions);
      sessionStorage.setItem("TARS_IS_INTERNAL", JSON.stringify(isInternal));

      // Step 4: Get accessible modules
      // Production modules always included — KWS check in Sidebar controls visibility.
      // TARS token deferred for everyone — fetched on-demand when KWS confirmed.
      const accessibleModules = getAccessibleModules(permissions);
      sessionStorage.setItem("TARS_MODULES_ACCESS", JSON.stringify(accessibleModules));

      // Step 5: Navigate — default to Invoice Management, then to any other
      // Bouncer-gated non-production module (e.g. Vendor Onboarding), then to
      // the first accessible module. Production modules come first in
      // TARS_MODULES but require KWS + a factory selection, so they're a poor
      // default for someone who doesn't have a permission-gated module.
      let redirectTo = REDIRECT_URI;
      let redirectQuery = REDIRECT_QUERY;
      const returnTo = resolveReturnTo(REDIRECT_QUERY, window.location.origin);
      if (returnTo) {
        redirectTo = returnTo.pathname;
        redirectQuery = returnTo.search;
      }
      if (redirectTo === "/") {
        const invoiceModule = accessibleModules.find(
          (m) => m.searchObj === "invoice-management"
        );
        const preferredModule =
          invoiceModule || accessibleModules.find((m) => !m.production);
        redirectTo = preferredModule
          ? preferredModule.link
          : accessibleModules.length > 0
            ? accessibleModules[0].link
            : "/dashboard/";
      }

      const path = [
        redirectTo + (redirectQuery ? redirectQuery : ""),
        queryString,
      ].join("#");

      // Replace, don't push: /callback must not remain in history, otherwise
      // browser/in-app back lands here and re-runs the OAuth exchange with an
      // already-consumed code (ENG-26629).
      navigate(path, { replace: true });
    }

    initAuth();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading)
    return (
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
        height={"100vh"}
      >
        <CircularProgress
          size={24}
          sx={{ color: COLOR_CONSTANTS["on-primary/variant"] }}
        />
      </Box>
    );

  if (error)
    return (
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"center"}
        height={"100vh"}
      >
        {error}
      </Box>
    );

  return <div></div>;
};
